import jsPDF from 'jspdf';
import 'jspdf-autotable';
import apiClient from '../../../services/api-client';
import StorageHelper from '../../../utils/storage-helper';
import { getPolicyStatusLabel } from './policyStatus';

const NAVY = [21, 57, 105];
const GOLD = [232, 169, 47];
const MARGIN = {left: 14, right: 14};

const BADGE_COLORS = {
    ACTIVE: {bg: [235, 247, 238], color: [40, 167, 69]},
    INACTIVE: {bg: [253, 237, 237], color: [220, 53, 69]},
    LAPSED: {bg: [255, 243, 205], color: [255, 152, 0]},
};
const DEFAULT_BADGE = {bg: [240, 240, 240], color: [108, 117, 125]};

const labelCell = (content) => ({
    content,
    styles: {fontStyle: 'bold', textColor: [120, 120, 120], fontSize: 8, fillColor: [250, 250, 250]}
});
const valueCell = (content, bold = false) => ({
    content,
    styles: bold ? {fontStyle: 'bold', fontSize: 10} : {fontSize: 10}
});

// Rows of [label, value] pairs become alternating label / value table rows (two columns).
const pairRows = (pairs) => pairs.flatMap(([left, right]) => [
    [labelCell(left.label), labelCell(right.label)],
    [valueCell(left.value, left.bold), valueCell(right.value, right.bold)],
]);

const fullName = (person) => `${person.Title || ''} ${person.First_Name || ''} ${person.Surname || ''}`.trim();

// Policies from the track endpoint may lack beneficiaries; fetch the full record when needed.
const needsFullPolicy = (policy, policyNumber) =>
    policyNumber && !policyNumber.includes('/') && policy.ExtendedFamiy === undefined && policy.Spouse === undefined;

export const fetchFullPolicy = async (policy, policyNumber) => {
    if (!needsFullPolicy(policy, policyNumber)) return policy;

    try {
        const response = await apiClient.get('policy/full/' + encodeURIComponent(policyNumber), {
            headers: {Authorization: 'Bearer ' + StorageHelper.get('token')}
        });
        const fetched = response.data.status === "SUCCESS" ? response.data.responseBody : null;
        if (Array.isArray(fetched)) {
            return fetched.find((p) => p.MainMember?.Policy_Number === policyNumber) || policy;
        }
        return fetched?.MainMember ? fetched : policy;
    } catch (error) {
        console.error("Failed to fetch full policy details:", error);
        return policy;
    }
};

const drawHeader = (doc, logo) => {
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, 210, 35, 'F');
    doc.setFillColor(...GOLD);
    doc.rect(0, 35, 210, 5, 'F');

    const textX = logo ? 45 : 14;
    if (logo) {
        doc.addImage(logo, 'PNG', 14, 5, 25, 25);
    }
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('DOVES', textX, 18);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('LIFE ASSURANCE', textX, 25);

    doc.setFontSize(9);
    doc.text('A dignified send-off, thoughtfully planned', 196, 20, {align: 'right'});
};

const drawTitle = (doc, member) => {
    const statusLabel = getPolicyStatusLabel(member.Status);

    doc.setTextColor(...NAVY);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('POLICY CERTIFICATE', 14, 55);

    doc.setFontSize(10);
    const statusWidth = doc.getTextWidth(statusLabel) + 12;
    const badge = BADGE_COLORS[statusLabel] || DEFAULT_BADGE;

    doc.setFillColor(...badge.bg);
    doc.setDrawColor(...badge.color);
    doc.roundedRect(196 - statusWidth, 48, statusWidth, 8, 2, 2, 'FD');
    doc.setTextColor(...badge.color);
    doc.text(statusLabel, 196 - (statusWidth / 2), 53.5, {align: 'center'});

    doc.setTextColor(100, 100, 100);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`${member.Product_Name || 'Doves Cash Plan Adult'} - customer policy summary`, 14, 62);
};

// Draws a section heading followed by a table; returns the Y position for the next section.
const drawSection = (doc, title, body, y) => {
    doc.setTextColor(...NAVY);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, y);

    doc.autoTable({startY: y + 4, theme: 'grid', body, margin: MARGIN});
    return doc.lastAutoTable.finalY + 15;
};

const drawSummary = (doc, member) => {
    doc.autoTable({
        startY: 70,
        theme: 'grid',
        headStyles: {fillColor: [245, 247, 250], textColor: [100, 100, 100], fontSize: 8, fontStyle: 'bold'},
        bodyStyles: {textColor: [0, 0, 0], fontSize: 11, fontStyle: 'bold'},
        head: [['POLICY NUMBER', 'POLICYHOLDER', 'INCEPTION DATE']],
        body: [[
            member.Policy_Number || '-',
            fullName(member),
            member.Inception_Date || member.Date_Joined || '-'
        ]],
        margin: MARGIN
    });
    return doc.lastAutoTable.finalY + 15;
};

const policyDetailsRows = (m) => {
    const premium = `US$${m.UsualPremium || '0.00'}`;
    return pairRows([
        [{label: 'PLAN', value: m.Product_Name || '-', bold: true}, {label: 'PAYMENT METHOD', value: m.Payment_Method || 'Cash'}],
        [{label: 'MAIN PLAN PREMIUM', value: premium}, {label: 'EXTENDED-FAMILY PREMIUM', value: 'US$0.00'}],
        [{label: 'TOTAL USUAL PREMIUM', value: premium, bold: true}, {label: 'MAIN-MEMBER WAITING PERIOD', value: '0 months recorded'}],
    ]);
};

const policyholderRows = (m) => pairRows([
    [{label: 'FULL NAME', value: fullName(m), bold: true}, {label: 'NATIONAL ID', value: m.ID_Number || '-'}],
    [{label: 'DATE OF BIRTH', value: m.Date_Of_Birth || '-'}, {label: 'GENDER', value: m.Gender || '-'}],
    [{label: 'RESIDENTIAL ADDRESS', value: m.Address || '-'}, {label: 'MOBILE NUMBER', value: m.Cell_Number || '-'}],
]);

const extendedRows = (ext) => pairRows([
    [{label: 'FULL NAME', value: `${ext.Name || ''} ${ext.Surname || ''}`.trim(), bold: true}, {label: 'RELATIONSHIP', value: ext.Relation || 'Relative'}],
    [{label: 'DATE OF BIRTH', value: ext.Date_of_birth || '-'}, {label: 'GENDER', value: ext.Gender || '-'}],
    [{label: 'PREMIUM', value: 'US$0.00'}, {label: 'WAITING PERIOD', value: '6 months'}],
]);

const drawFooter = (doc) => {
    const pageHeight = doc.internal.pageSize.height;
    doc.setFillColor(...NAVY);
    doc.rect(0, pageHeight - 15, 210, 15, 'F');
    doc.setFillColor(...GOLD);
    doc.rect(0, pageHeight - 16, 210, 1, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text('157 & 159 Harare St, Harare  |  +263 242 774013/6  |  enquiries@doves.co.zw  |  doves.co.zw', 105, pageHeight - 7, {align: 'center'});
};

export const drawCertificate = (policy, logo) => {
    const member = policy.MainMember || policy;
    const doc = new jsPDF();

    drawHeader(doc, logo);
    drawTitle(doc, member);

    let y = drawSummary(doc, member);
    y = drawSection(doc, 'Policy details', policyDetailsRows(member), y);
    y = drawSection(doc, 'Policyholder details', policyholderRows(member), y);
    if (policy.ExtendedFamiy?.length > 0) {
        drawSection(doc, 'Extended-family member', extendedRows(policy.ExtendedFamiy[0]), y);
    }

    drawFooter(doc);

    const safePolicyNumber = String(member.Policy_Number || '').replaceAll('/', '-');
    doc.save(`Policy Certificate ${safePolicyNumber}.pdf`.trim());
};
